"use client";

import { Mail, MapPin, Phone } from "lucide-react";
import { Cta } from "./cta";
import { MaskedHeadline, Reveal, EngravedLine } from "./motion-primitives";
import { useQuote } from "./quote-context";
import { BrandLogo } from "./brand-logo";

export function FinalCtaFooter() {
  const { goToQuote } = useQuote();

  return (
    <footer className="border-t border-ink/10 bg-softwhite pt-24 pb-28 sm:pb-16">
      <div className="mx-auto max-w-[1400px] px-5">
        <h2 className="max-w-[16ch] font-display text-[clamp(2.4rem,6.5vw,5.5rem)] leading-[0.92] font-bold text-ink">
          <MaskedHeadline lines={["Your idea deserves more", "than a generic solution."]} />
        </h2>
        <Reveal delay={0.12}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground">
            Let&rsquo;s find the right product and create an engraved result that feels
            personal, professional, and made for its purpose.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Cta variant="ink" onClick={() => goToQuote()}>Request a Custom Quote</Cta>
            <a
              href="tel:+12047613698"
              className="inline-flex items-center gap-2.5 rounded-full border border-ink/25 px-6 py-3.5 text-sm font-semibold text-ink transition hover:bg-ink hover:text-softwhite"
            >
              <Phone className="size-4" aria-hidden /> Call 204-761-3698
            </a>
          </div>
        </Reveal>

        <EngravedLine className="mt-20" />

        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <BrandLogo className="h-16 w-auto" sizes="200px" loading="lazy" />
            <p className="mt-3 text-sm text-muted-foreground">Family-owned since 2019</p>
          </div>

          <address className="space-y-3 text-sm not-italic text-muted-foreground">
            <p className="flex items-start gap-2.5">
              <MapPin className="mt-0.5 size-4 shrink-0 text-brass-deep" aria-hidden />
              Stockton, Manitoba, Canada
            </p>
            <p className="flex items-center gap-2.5">
              <Phone className="size-4 shrink-0 text-brass-deep" aria-hidden />
              <a href="tel:+12047613698" className="hover:text-ink">
                204-761-3698
              </a>
            </p>
            <p className="flex items-center gap-2.5">
              <Mail className="size-4 shrink-0 text-brass-deep" aria-hidden />
              <a href="mailto:millsengraving@gmail.com" className="hover:text-ink">
                millsengraving@gmail.com
              </a>
            </p>
          </address>

          <nav aria-label="Footer" className="space-y-3 text-sm text-muted-foreground">
            {[
              ["Products & Services", "#categories"],
              ["Our Work", "#work"],
              ["About", "#story"],
              ["Request a Quote", "#quote"],
            ].map(([label, href]) => (
              <a key={label} href={href} className="block hover:text-ink">
                {label}
              </a>
            ))}
          </nav>

          <div className="space-y-3 text-sm text-muted-foreground">
            <p className="eyebrow text-brass-deep">Coming soon</p>
            <p>Privacy Policy</p>
            <p>Terms of Use</p>
            <p>Social profiles</p>
          </div>
        </div>

        <p className="mt-14 border-t border-ink/10 pt-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Mills Engraving. All rights reserved.
        </p>
      </div>
    </footer>
  );
}